import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    makeStyles,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import {Loading, SwipeStyledButton} from "@cabinet-packages/components/index";
import {getScheduleByIdPunkt} from "@cabinet-packages/api/api";
import {
    getPunkts,
    getSchedule,
    setAssayLine,
    setAssayTime,
    setOrderDate
} from "@cabinet-packages/redux/actions/actionsCreators";
import {useDispatch, useSelector} from "react-redux";
import MonthView from "@cabinet-packages/pages/CalendarMedis/components/MonthView";
import HoursView from "@cabinet-packages/pages/CalendarMedis/components/HoursView";
import moment from "moment";

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        '@media(max-width: 900px)': {
            flexDirection: 'column',
            alignItems: 'center',
            columnGap: '5px'
        }
    },
    inputRoot: {
        fontWeight: 600,
        padding: '10px',
        borderBottom: 'none',
        backgroundColor: '#f6f5f5',
    },
    describeInput: {
        padding: '10px',
        border: `1px solid #e8e8e8`,
        backgroundColor: "white",
    },
    inputLabel: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontSize: '12px',
        fontWeight: 500,
        margin: '10px'
    },
    itemContainer: {
        backgroundColor: '#f6f5f5',
        margin: theme.spacing(1)
    },
    orderContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 4fr 4fr',
        height: '40px',
    },
    serviceContainer: {
        display: 'grid',
        gridTemplateColumns: '6fr 1.5fr',
        margin: '10px 0px',
        position: 'relative'
    },
    columnLabelsContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 4fr 4fr',
        height: '15px',
    },
    serviceLabelsContainer: {
        display: 'grid',
        gridTemplateColumns: '6fr 1.5fr',
        height: '15px',
    },
    columnLabel: {
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.grey.text
    },
    orderDetailLabel: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 600
    },
    formControlLabelRoot: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '30px',
        '@media(max-width: 900px)': {
            marginTop: 0
        }
    },
    dialogContainer: {
        '& .MuiPaper-rounded': {
            borderRadius: '20px'
        },
    },
    dialogContent: {
        margin: '30px 30px 0px 30px'
    },
    codeBlock: {
        padding: '30px',
        boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
        borderRadius: '20px',
        width: 'min-content',
        margin: 'auto'
    },
    dialogTitle: {
        backgroundColor: theme.palette.primary.dark,
        textAlign: 'center',
        color: 'white',
    },
    dialogText: {
        fontWeight: '500',
        fontSize: '15px',
        //width: '40%',
        '@media(max-width: 600px': {
            width: '80%'
        },
        textAlign: 'center',
        marginTop: '30px'
    },
    loading: {
        marginTop: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
    },
}));


const ukMonths = ["січень", "лютий", "березень", "квітень", "травень", "червень",
    "липень", "серпень", "вересень", "жовтень", "листопад", "грудень"];

moment.locale('uk');

const EditDateDialog = ({ handleClose, order, service}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const mdUp = useMediaQuery(theme => theme.breakpoints.up('md'))
    const { schedule } = useSelector(state => state.scheduleOfOrders);
    const { selectedPunkt, time, timeLine } = useSelector(state => state.orderAssays);
    const [scheduleForSelectedPunkt, setScheduleForSelectedPunkt] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(moment().format('MMMM'));
    const [monthDays, setMonthDays] = useState(null);
    const [selectedYear, setSelectedYear] = useState(moment().format('YYYY'));
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [checked, setChecked] = useState(false);
    const ref = useRef();

    const handleChange = (event) => {
        setChecked(event.target.checked);
        if (event.target.checked) {
            dispatch(setAssayLine({date: 'Ви не обрали час забору.'}));
            setSelectedDay(null);
            setSelectedTime(null);
        } else {
            dispatch(setAssayLine());

            const foundFirstFreeDay = monthDays.flat(1).find(day => day.freeHours && day.current);

            setSelectedDay(foundFirstFreeDay || null);
        }
    };

    const handleChangeMonth = (e, isBack, dontClearDay = true) => {
        e.stopPropagation();
        e.preventDefault();

        const idxOfSelectedMonth = ukMonths.indexOf(selectedMonth);
        const lastMonthNumber = ukMonths.length - 1;

        setSelectedTime(null);
        dispatch(setAssayTime());

        if (!dontClearDay) {
            setSelectedDay(null);
        }

        if (isBack) {
            if (idxOfSelectedMonth === 0) {
                setSelectedMonth(ukMonths[lastMonthNumber]);
                setSelectedYear(''+(+selectedYear-1));
            } else {
                setSelectedMonth(ukMonths[idxOfSelectedMonth-1]);
            }
        } else {
            if (idxOfSelectedMonth === lastMonthNumber) {
                setSelectedMonth(ukMonths[0]);
                setSelectedYear((+selectedYear+1)+'');
            } else {
                setSelectedMonth(ukMonths[idxOfSelectedMonth+1]);
            }
        }
    }

    useEffect(() => {
        dispatch(getSchedule());
    }, [dispatch]);

    const handleSchedule = useCallback(() => {
        if (schedule && selectedPunkt) {
            setScheduleForSelectedPunkt(schedule.filter(item => item.idPunkt === selectedPunkt.idPunkt));
        }
    }, [schedule, selectedPunkt]);

    const handleMonthDays = useCallback(async () => {
        if (selectedYear && selectedMonth && scheduleForSelectedPunkt && selectedPunkt && selectedPunkt.takingHours && selectedPunkt.takingHours.length > 0) {
            const data = await getScheduleByIdPunkt(selectedPunkt.idPunkt);

            const monthFirstDay = moment().year(+selectedYear).month(selectedMonth).startOf('month').weekday();
            const lengthOfMonth = moment().year(+selectedYear).month(selectedMonth).endOf('month').date();
            const monthLastDay = moment().year(+selectedYear).month(selectedMonth).endOf('month').weekday();

            const arrayOfDays = [];
            for (let i = 0; i < monthFirstDay; i++) {//prev month days
                const year = ukMonths.indexOf(selectedMonth.toLowerCase()) === 0
                    ? +selectedYear - 1
                    : +selectedYear;

                const prevMonth = ukMonths.indexOf(selectedMonth.toLowerCase()) === 0
                    ? ukMonths[11].toLowerCase()
                    : ukMonths[ukMonths.indexOf(selectedMonth.toLowerCase()) - 1].toLowerCase();

                const dayOfPrevMonth = moment()
                    .year(+year)
                    .month(selectedMonth).subtract(1, 'months').endOf('month')
                    .subtract(monthFirstDay - (i + 1), 'days').date();

                const hours = Object.keys(selectedPunkt.takingHours[i])
                    .map(item => selectedPunkt.takingHours[i][item])[0];

                const hoursSchedule = hours.sceduler.map(item => {
                    const free = scheduleForSelectedPunkt.reduce((acu, cur) => {
                        const reservedDate = moment(cur.dt, 'DD.MM.YYYY HH:mm');

                        const isFree = (reservedDate.format('MMMM').toLowerCase() === moment(prevMonth, 'MMMM').format('MMMM').toLowerCase())
                            && +reservedDate.format('YYYY') === +year
                            && +reservedDate.format('D') === +dayOfPrevMonth
                            && reservedDate.format('HH:mm') === item;

                        if (isFree) {
                            return acu + 1;
                        } else {
                            return acu;
                        }

                    }, 0);

                    const splitTime = item.split(':');

                    const isPastDay = moment().add(1, 'h').isBefore(
                        moment()
                            .set('year', +year)
                            .set('month', ukMonths.indexOf(prevMonth))
                            .set("date", +dayOfPrevMonth)
                            .set('hour', +splitTime[0])
                            .set('minute', +splitTime[1])
                    );

                    return ({
                        time: item,
                        free: (free < selectedPunkt.patientCount+1) && isPastDay
                    })
                });

                arrayOfDays.push({dayNum: dayOfPrevMonth,
                    current: false,
                    prev: true,
                    freeHours: hoursSchedule.reduce((acu, cur) => {
                        if (acu) {
                            return acu;
                        } else {
                            return cur.free;
                        }
                    }, false),
                    scheduler: hoursSchedule,
                });
            }
            for (let i = 1; i <= lengthOfMonth; i++) {//current month days
                const hours = Object.keys(selectedPunkt.takingHours[(monthFirstDay+i-1)%7])
                    .map(item => selectedPunkt.takingHours[(monthFirstDay+i-1)%7][item])[0];

                const hoursSchedule = hours.sceduler.map(item => {
                    const free = scheduleForSelectedPunkt.reduce((acu, cur) => {
                        const reservedDate = moment(cur.dt, 'DD.MM.YYYY HH:mm');

                        const isFree = (reservedDate.format('MMMM').toLowerCase() === selectedMonth.toLowerCase())
                            && +reservedDate.format('YYYY') === +selectedYear
                            && +reservedDate.format('D') === +i
                            && reservedDate.format('HH:mm') === item;

                        if (isFree) {
                            return acu + 1;
                        } else {
                            return acu;
                        }
                    }, 0);

                    const splitTime = item.split(':');

                    const isPastDay = moment().add(1, 'h').isBefore(
                        moment()
                            .set('year', +selectedYear)
                            .set('month', ukMonths.indexOf(selectedMonth))
                            .set("date", +i)
                            .set('hour', +splitTime[0])
                            .set('minute', +splitTime[1])
                    );

                    return ({
                        time: item,
                        free: (free < selectedPunkt.patientCount+1) && isPastDay
                    })
                });

                arrayOfDays.push({dayNum: i,
                    current: true,
                    freeHours: hoursSchedule.reduce((acu, cur) => {
                        if (acu) {
                            return acu;
                        } else {
                            return cur.free;
                        }
                    }, false),
                    scheduler: hoursSchedule,
                });
            }
            for (let i = 1; i < 7 - monthLastDay; i++) {//next month days
                const nextMonth = ukMonths.indexOf(selectedMonth.toLowerCase()) === 11
                    ? ukMonths[0].toLowerCase()
                    : ukMonths[ukMonths.indexOf(selectedMonth.toLowerCase()) + 1].toLowerCase();

                const year = ukMonths.indexOf(selectedMonth.toLowerCase()) === 11
                    ? +selectedYear + 1
                    : selectedYear;

                const hours = Object.keys(selectedPunkt.takingHours[monthLastDay + i])
                    .map(item => selectedPunkt.takingHours[monthLastDay + i][item])[0];

                const hoursSchedule = hours.sceduler.map(item => {
                    const free = scheduleForSelectedPunkt.reduce((acu, cur) => {
                        const reservedDate = moment(cur.dt, 'DD.MM.YYYY HH:mm');

                        const isFree = (reservedDate.format('MMMM').toLowerCase() === moment(nextMonth, 'MMMM').format('MMMM').toLowerCase())
                            && +reservedDate.format('YYYY') === +year
                            && +reservedDate.format('D') === +i
                            && reservedDate.format('HH:mm') === item;

                        if (isFree) {
                            return acu + 1;
                        } else {
                            return acu;
                        }

                    }, 0);

                    const splitTime = item.split(':');

                    const isPastDay = moment().isBefore(
                        moment()
                            .set('year', +year)
                            .set('month', ukMonths.indexOf(nextMonth))
                            .set("date", +i)
                            .set('hour', +splitTime[0])
                            .set('minute', +splitTime[1])
                    );

                    return ({
                        time: item,
                        free: (free < selectedPunkt.patientCount+1) && isPastDay
                    })
                });

                arrayOfDays.push({dayNum: i, current: false, next: true,
                    freeHours: hoursSchedule.reduce((acu, cur) => {
                        if (acu) {
                            return acu;
                        } else {
                            return cur.free;
                        }
                    }, false),
                    scheduler: hoursSchedule
                });
            }

            const workHoursSchedule = (data[0] && JSON.parse(data[0].workHours)) || [];

            const refactoredArrayOfDays = arrayOfDays
                .map(item => {

                    if (item.prev && item.freeHours && workHoursSchedule) { //prev month
                        const prevMonth = ukMonths.indexOf(selectedMonth.toLowerCase()) === 0
                            ? ukMonths[11].toLowerCase()
                            : ukMonths[ukMonths.indexOf(selectedMonth.toLowerCase()) - 1].toLowerCase();

                        const foundSchedule = workHoursSchedule.find(scheduledTime => (
                            moment().year(+ukMonths.indexOf(selectedMonth.toLowerCase()) === 0 ? +selectedYear-1 : +selectedYear).month(prevMonth).date(item.dayNum).format('YYYY-MM-DD')
                            === scheduledTime.dt_start.slice(0,10)
                        ))

                        if (!foundSchedule) {
                            return ({
                                ...item,
                                freeHours: false,
                                scheduler: item.scheduler.map(day => ({...day, free: false}))
                            })
                        } else {
                            const scheduler = item.scheduler.map((day) => {
                                if (+day.time.slice(0, 2) >= +foundSchedule.dt_start.slice(11,13) && +day.time.slice(0, 2) <= +foundSchedule.dt_end.slice(11,13)) {
                                    return day;
                                } else {
                                    return ({...day, free: false});
                                }
                            })

                            return ({
                                ...item,
                                scheduler,
                                freeHours: scheduler.reduce((acu, cur) => {
                                    if (acu) {
                                        return acu;
                                    } else {
                                        return cur.free;
                                    }
                                }, false),
                            });
                        }
                    } else if (item.next && item.freeHours && workHoursSchedule) { //next month
                        const nextMonth = ukMonths.indexOf(selectedMonth.toLowerCase()) === 11
                            ? ukMonths[0].toLowerCase()
                            : ukMonths[ukMonths.indexOf(selectedMonth.toLowerCase()) + 1].toLowerCase();

                        const foundSchedule = workHoursSchedule.find(scheduledTime => (
                            moment().year(ukMonths.indexOf(selectedMonth.toLowerCase()) === 11 ? +selectedYear+1 : +selectedYear).month(nextMonth).date(item.dayNum).format('YYYY-MM-DD')
                            === scheduledTime.dt_start.slice(0,10)
                        ))

                        if (!foundSchedule) {
                            return ({
                                ...item,
                                freeHours: false,
                                scheduler: item.scheduler.map(day => ({...day, free: false}))
                            })
                        } else {
                            const scheduler = item.scheduler.map((day) => {
                                if (+day.time.slice(0, 2) >= +foundSchedule.dt_start.slice(11,13) && +day.time.slice(0, 2) <= +foundSchedule.dt_end.slice(11,13)) {
                                    return day;
                                } else {
                                    return ({...day, free: false});
                                }
                            })
                            return ({
                                ...item,
                                scheduler,
                                freeHours: scheduler.reduce((acu, cur) => {
                                    if (acu) {
                                        return acu;
                                    } else {
                                        return cur.free;
                                    }
                                }, false),
                            });
                        }
                    } else if (item.current && item.freeHours && workHoursSchedule) { //current month
                        const foundSchedule = workHoursSchedule.find(scheduledTime => {

                            return (moment().year(+selectedYear).month(selectedMonth.toLowerCase()).date(+item.dayNum).format('YYYY-MM-DD')
                                === scheduledTime.dt_start.slice(0, 10)
                            )
                        })

                        if (!foundSchedule) {
                            return ({
                                ...item,
                                freeHours: false,
                                scheduler: item.scheduler.map(day => ({...day, free: false}))
                            })
                        } else {
                            const scheduler = item.scheduler.map((day) => {
                                if (+day.time.slice(0, 2) >= +foundSchedule.dt_start.slice(11,13) && +day.time.slice(0, 2) <= +foundSchedule.dt_end.slice(11,13)) {
                                    return day;
                                } else {
                                    return ({...day, free: false});
                                }
                            })

                            return ({
                                ...item,
                                scheduler,
                                freeHours: scheduler.reduce((acu, cur) => {
                                    if (acu) {
                                        return acu;
                                    } else {
                                        return cur.free;
                                    }
                                }, false),
                            });
                        }
                    } else {
                        return item;
                    }
                });

            setMonthDays(refactoredArrayOfDays
                .reduce((acu, cur, idx) => {
                    if (idx === 0) return [[cur]];
                    if ((idx) % 7 === 0) return [...acu, [cur]];
                    acu[Math.ceil(((idx + 1) / 7) - 1)] = [...acu[Math.ceil(((idx + 1) / 7) - 1)], cur]
                    return acu;
                }, []));
            if (!selectedDay) {
                const foundFirstFreeDay = refactoredArrayOfDays.find(day => day.freeHours && day.current);

                if (foundFirstFreeDay) {
                    setSelectedDay(foundFirstFreeDay);
                } else if (!timeLine) {
                    handleChange({target: {checked: true}});
                }
            }
        }
    },[selectedYear, selectedMonth, scheduleForSelectedPunkt, selectedPunkt]);

    useEffect(() => {
        handleSchedule();
    }, [handleSchedule]);

    useEffect(() => {
        handleMonthDays();
    }, [handleMonthDays]);

    useEffect(() => {
        dispatch(getPunkts(true));
    }, []);

    useEffect(() => {
        if (selectedYear && selectedDay && selectedMonth && selectedTime) {
            dispatch(setAssayTime({
                date: {
                    time: `${selectedDay.dayNum} ${selectedMonth} ${selectedYear} року, ${selectedTime.time}`,
                        moment: moment(`${selectedDay.dayNum} ${selectedMonth} ${selectedYear} ${selectedTime.time}`, 'DD MMMM YYYY hh:mm'),
                    selectedDay,
                    selectedTime,
                    selectedMonth,
                    selectedYear,
                },
            }, true));
        }
    }, [selectedYear, selectedDay, selectedMonth, selectedTime]);

    useEffect(() => {
        if (time) {
            if (time.moment.isBefore(moment().add(1,'hours'))) {
                dispatch(setAssayTime());
            } else {
                setSelectedYear(time.selectedYear);
                setSelectedMonth(time.selectedMonth);
                setSelectedDay(time.selectedDay);
                setSelectedTime(time.selectedTime);
            }
        }
    }, []);

    useEffect(() => {
        if (timeLine && monthDays) {
            handleChange({target: {checked: true}});
        }
    }, [monthDays]);

    const handleDateConfirm = () => {
        //console.log(`${selectedYear}-${('0'+ukMonths.indexOf(selectedMonth))}-${selectedDay.dayNum} ${selectedTime.time}:00.0`)
        dispatch(setOrderDate(order.id, `${selectedYear}-${('0'+ukMonths.indexOf(selectedMonth))}-${selectedDay.dayNum} ${selectedTime.time}:00.0`))
        handleClose()
    }

    console.log(monthDays)

    return (
        <Dialog
            open={true}
            onClose={() => handleClose()}
            PaperProps={{
                style: { borderRadius: 10,
                    minWidth: mdUp ? (selectedDay && monthDays ? '1100px' : '800px') : '98%',
                    minHeight: mdUp ? '800px' : '700px'
                }
            }}
        >
            <DialogTitle className={classes.dialogTitle}>
                РЕДАГУВАННЯ ДАТИ ТА ЧАСУ
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {monthDays !== null ? (
                    <>
                        <div className={classes.root}>
                            {monthDays
                                && <MonthView monthDays={monthDays} handleChangeMonth={handleChangeMonth} selectedMonth={selectedMonth}
                                              selectedYear={selectedYear} useDay={[selectedDay, setSelectedDay]}
                                              setSelectedTime={setSelectedTime} checked={checked} scrollRef={ref}/>}
                            <div ref={ref}/>
                            {selectedDay && monthDays && <HoursView selectedDay={selectedDay} useTime={[selectedTime, setSelectedTime]}/>}
                        </div>
                        {monthDays && (
                            <FormControlLabel
                                classes={{root: classes.formControlLabelRoot}}
                                control={<Checkbox
                                    color="primary"
                                    size="small"
                                    checked={checked}
                                    onChange={handleChange} />}
                                label={
                                    <>
                                        <Typography style={{fontWeight: 400, fontSize: 15}}>
                                            Я хочу пропустити крок з вибором дати та часу
                                            <br/>
                                            <Typography style={{fontWeight: 400, fontSize: 12}}>
                                                (у випадку, якщо Ви не впевнені коли зможете прийти та здати аналізи)
                                            </Typography>
                                        </Typography>
                                    </>
                                }
                                labelPlacement="end"
                                className={classes.formControl}
                            />
                        )}
                    </>
                ) : (
                    <div className={classes.loading}>
                        <Typography style={{fontWeight: 500}} align={'center'}>
                            Завантаження даних, будь ласка, зачекайте.
                        </Typography>
                        <Loading/>
                    </div>
                )}
            </DialogContent>
            <DialogActions style={{display: 'flex', justifyContent: 'center', margin: '20px 0'}}>
                <SwipeStyledButton
                    animationStart={'right'}
                    animationMaterialColor={'#ff2121'}
                    buttonMaterialColor={'#ffffff'}
                    hoverTextColor={'#ffffff'}
                    border={"1px solid #0E4E4E"}
                    onClick={handleClose}
                >
                    СКАСУВАТИ
                </SwipeStyledButton>
                <SwipeStyledButton
                    animationStart={'right'}
                    //animationMaterialColor={'rgba(0,140,127,0.67)'}
                    buttonMaterialColor={'#FFC101'}
                    hoverTextColor={'#ffffff'}
                    onClick={handleDateConfirm}
                    style={{color: '#0E4E4E'}}
                >
                    ПІДТВЕРДИТИ
                </SwipeStyledButton>
            </DialogActions>
        </Dialog>
    );
}

export default EditDateDialog;